import {
  IllegalStateError,
  isDeltaBaseError,
  ValidationError,
} from '@delta-base/toolkit';
import { createMiddleware } from 'hono/factory';
import { HTTPException } from 'hono/http-exception';
import { ZodError } from 'zod';
import type { ContextSchemaConfig, ErrorResponsePattern } from './config';
import { createObservabilityConfig, observabilityConfig } from './config';
import { initializeTracing } from './tracing';
import type { BaseHonoBindings, BaseHonoVariables } from './types/hono';
import { initializeBuildInfo, WideEventsWrapper } from './wide-events';

/**
 * Known error response patterns that we can detect and track.
 *
 * This registry provides a clean, extensible way to detect different types of
 * error responses and track them with appropriate context and categorization.
 * Each pattern includes a matcher function and a tracker function that properly
 * categorizes errors as expected (user/client errors) or unexpected (system errors).
 *
 * Note: Error patterns are now defined in config.ts and can be customized.
 */

/**
 * Registry of error response patterns for clean detection and tracking.
 *
 * This registry enables clean, maintainable error detection by:
 * - Separating error detection logic from tracking logic
 * - Properly categorizing errors as expected vs unexpected
 * - Providing extensible pattern matching for different error types
 * - Avoiding brittle if-statement chains
 *
 * Expected errors (unexpected: false) include:
 * - Validation errors (user submitted invalid data)
 * - Business rule violations (duplicate email, insufficient permissions)
 * - Authentication/authorization failures
 * - Rate limiting
 *
 * Unexpected errors (unexpected: true) include:
 * - System failures (database down, network issues)
 * - Programming errors (null pointer exceptions, type errors)
 * - Infrastructure issues (service unavailable, timeouts)
 * - Unhandled edge cases
 *
 * Note: Error patterns are now configurable via the observability config system.
 * Use observabilityConfig.addErrorPattern() to add custom patterns.
 */

/**
 * Configuration options for the observability middleware (simplified interface)
 *
 * For more advanced configuration, use configureObservabilityAdvanced()
 * or import and modify the observabilityConfig directly.
 */
export interface ObservabilityOptions {
  /**
   * Custom service name (defaults to environment variable SERVICE_NAME)
   */
  serviceName?: string;

  /**
   * Whether to automatically track HTTP request/response (defaults to true)
   */
  autoTrackHttp?: boolean;

  /**
   * Whether to automatically detect and track error responses (defaults to true)
   */
  autoTrackErrorResponses?: boolean;

  /**
   * Whether to enable debug logging (defaults to false)
   */
  debug?: boolean;

  /**
   * Build info to initialize (optional)
   */
  buildInfo?: Record<string, string | number | boolean>;
}

/**
 * Middleware that automatically sets up wide events tracking for all routes.
 *
 * This middleware:
 * - Initializes OpenTelemetry tracing
 * - Creates a WideEventsWrapper for the request
 * - Automatically tracks HTTP request/response details
 * - Detects and tracks error responses using pattern matching
 * - Makes the tracker available via `c.get('wideEvent')`
 * - Provides a simple fallback for operation type derivation
 *
 * @param options - Configuration options for the middleware
 * @returns Hono middleware function
 *
 * @example
 * ```typescript
 * const app = new OpenAPIHono();
 * app.use('*', observabilityMiddleware());
 *
 * // Optional: Add the error handler for exceptions that bypass validation
 * app.onError(createObservabilityErrorHandler());
 *
 * // Set operation type explicitly in your route handlers:
 * app.post('/users/:id/change-password', async (c) => {
 *   const tracker = c.get('wideEvent');
 *   tracker.setOperationType('user.change-password');
 *   // ... route logic
 * });
 * ```
 */
export function observabilityMiddleware(options: ObservabilityOptions = {}) {
  // Initialize build info if provided
  if (options.buildInfo) {
    initializeBuildInfo(options.buildInfo);
  }

  // Apply simple options to the global config
  if (Object.keys(options).length > 0) {
    const config = createObservabilityConfig(options);
    observabilityConfig.updateConfig(config);
  }

  // Get the current configuration
  const config = observabilityConfig.getConfig();
  const serviceName = config.tracing.serviceName || 'unknown-service';
  const autoTrackHttp = config.http.trackRequests && config.http.trackResponses;
  const autoTrackErrorResponses = config.errors.autoTrackErrorResponses;

  return createMiddleware<{
    Bindings: BaseHonoBindings;
    Variables: BaseHonoVariables;
  }>(async (c, next) => {
    // Initialize tracing once per request
    initializeTracing();

    // Auto-derive operation details from route
    const method = c.req.method;
    const path = c.req.path;
    const operationName = `${method} ${path}`;
    const operationType = simpleOperationTypeDerivation(method, path);

    // Create wide event wrapper
    const wideEvent = new WideEventsWrapper(
      operationName,
      operationType,
      serviceName
    );

    // Automatically track HTTP request details
    if (autoTrackHttp) {
      wideEvent.trackHttpRequest(c.req.raw);
    }

    // Store in context for route handlers
    c.set('wideEvent', wideEvent);

    await wideEvent.execute(async () => {
      await next();

      // Automatically track HTTP response details
      if (autoTrackHttp && c.res) {
        wideEvent.trackHttpResponse(c.res);
      }

      // Check for error responses and track them
      if (autoTrackErrorResponses && c.res && isErrorResponse(c.res.status)) {
        await detectAndTrackErrorResponse(wideEvent, c.res);
      }
    });
  });
}

/**
 * Simple operation type derivation as a fallback.
 *
 * This is just a basic fallback - you should set the operation type
 * explicitly in your route handlers using tracker.setOperation().
 *
 * @param method - HTTP method
 * @param path - Request path
 * @returns Simple operation type
 */
function simpleOperationTypeDerivation(method: string, path: string): string {
  // Extract first path segment as resource
  const resourceMatch = path.match(/^\/([^/?]+)/);
  let resource = resourceMatch?.[1] || 'unknown';

  // Convert plural resources to singular for consistent operation naming
  if (resource.endsWith('s') && resource.length > 1) {
    resource = resource.slice(0, -1);
  }

  // Simple method mapping
  const methodMap: Record<string, string> = {
    GET: 'read',
    POST: 'create',
    PUT: 'update',
    PATCH: 'update',
    DELETE: 'delete',
  };

  const action = methodMap[method.toUpperCase()] || 'operation';
  return `${resource}.${action}`;
}

/**
 * Check if a status code indicates an error response
 */
function isErrorResponse(status: number): boolean {
  return status >= 400;
}

/**
 * Detect and track error responses using pattern matching.
 *
 * This function uses a registry of error patterns to cleanly detect
 * different types of error responses and track them appropriately.
 * It provides proper error categorization (expected vs unexpected) and
 * avoids the need for brittle if-statement chains.
 *
 * The function:
 * 1. Clones the response to avoid consuming the original body
 * 2. Parses the response as JSON (only processes JSON responses)
 * 3. Matches against known error patterns in order
 * 4. Tracks the first matching pattern with proper categorization
 * 5. Falls back to generic error tracking based on HTTP status codes
 *
 * Error categorization:
 * - 4xx responses: Expected errors (user/client errors)
 * - 5xx responses: Unexpected errors (system/server errors)
 *
 * @param wideEvent - Wide events wrapper to track error details
 * @param response - HTTP response to check for error patterns
 */
async function detectAndTrackErrorResponse(
  wideEvent: WideEventsWrapper,
  response: Response
): Promise<void> {
  try {
    // Only process JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      return;
    }

    // Clone the response to avoid consuming the original body
    const clonedResponse = response.clone();
    const responseBody = await clonedResponse.json();

    // Try to match against known error patterns
    const config = observabilityConfig.getConfig();
    for (const pattern of config.errors.errorPatterns) {
      if (pattern.matcher(responseBody)) {
        console.log(`Detected ${pattern.name} error response, tracking...`);
        pattern.tracker(wideEvent, responseBody);
        return; // Stop after first match
      }
    }

    // If no pattern matches, track as generic error
    // Categorize based on HTTP status - 4xx are often expected, 5xx are unexpected
    const isClientError = response.status >= 400 && response.status < 500;
    wideEvent.trackError({
      type: 'UnknownError',
      message: `HTTP ${response.status}`,
      httpStatus: response.status,
      unexpected: !isClientError, // 4xx = expected (client error), 5xx = unexpected (server error)
    });
  } catch (error) {
    // If we can't parse the response, just ignore it
    console.log('Failed to parse response body for error detection:', error);
  }
}

/**
 * Create a Hono error handler that integrates with observability tracking.
 *
 * This handler should be used with app.onError() to catch exceptions
 * that bypass the normal response flow (like thrown errors in middleware).
 *
 * @param options - Configuration options
 * @returns Hono error handler function
 *
 * @example
 * ```typescript
 * const app = new OpenAPIHono();
 * app.use('*', observabilityMiddleware());
 * app.onError(createObservabilityErrorHandler());
 * ```
 */
export function createObservabilityErrorHandler(
  options: ObservabilityOptions = {}
) {
  // Apply simple options to the global config if provided
  if (Object.keys(options).length > 0) {
    const config = createObservabilityConfig(options);
    observabilityConfig.updateConfig(config);
  }

  // Get the current configuration
  const config = observabilityConfig.getConfig();
  const serviceName = config.tracing.serviceName || 'unknown-service';

  // biome-ignore lint/suspicious/noExplicitAny: result context could be anything
  return (error: Error, c: any) => {
    // Try to get the existing tracker from context
    const wideEvent = c.get('wideEvent') as WideEventsWrapper | undefined;

    if (wideEvent) {
      // Track the error using the existing tracker
      trackErrorWithContext(wideEvent, error);
    } else {
      // Create a minimal tracker for error-only tracking
      const method = c.req.method;
      const path = c.req.path;
      const operationName = `${method} ${path}`;
      const operationType = simpleOperationTypeDerivation(method, path);

      const errorTracker = new WideEventsWrapper(
        operationName,
        operationType,
        serviceName
      );

      // Track the error
      trackErrorWithContext(errorTracker, error);

      // Complete the tracking immediately since this is error-only
      errorTracker
        .execute(async () => {
          // No-op, just complete the tracking
        })
        .catch(console.error);
    }

    // Return appropriate response based on error type
    return createErrorResponse(error, c);
  };
}

/**
 * Track an error with appropriate context and categorization.
 *
 * This function properly categorizes errors into expected and unexpected types:
 *
 * **Expected Errors (unexpected: false):**
 * - ValidationError: User submitted invalid data
 * - BusinessValidationError: Business rule violations (duplicate email, etc.)
 * - IllegalStateError: Business constraint violations (already exists, etc.)
 * - Authentication/authorization failures
 * - Rate limiting errors
 *
 * **Unexpected Errors (unexpected: true):**
 * - System failures (database down, network issues)
 * - Programming errors (null pointer exceptions, type errors)
 * - Infrastructure issues (service unavailable, timeouts)
 * - Generic/unknown errors that indicate potential bugs
 *
 * This categorization enables:
 * - Proper alerting (only alert on unexpected errors)
 * - SLA/Error budget calculations (only count unexpected errors)
 * - User experience tracking (track expected errors separately)
 * - Debugging prioritization (unexpected errors need immediate attention)
 *
 * @param wideEvent - Wide events wrapper to track error details
 * @param error - The error that occurred
 */
function trackErrorWithContext(
  wideEvent: WideEventsWrapper,
  error: Error
): void {
  if (error instanceof ZodError) {
    // Handle Zod validation errors - these are EXPECTED (user input errors)
    wideEvent.trackError({
      type: 'ValidationError',
      message: 'Request validation failed',
      validation: {
        issueCount: error.issues.length,
        fields: error.issues.map((issue) => issue.path.join('.')),
      },
      unexpected: false, // Expected - user submitted invalid data
    });
  } else if (error instanceof ValidationError) {
    // Handle business validation errors - these are EXPECTED (business rule failures)
    wideEvent.trackError({
      type: 'BusinessValidationError',
      message: error.message,
      businessRule: {
        rule: 'business_validation',
        violation: error.message,
      },
      unexpected: false, // Expected - business rule violation
    });
  } else if (error instanceof IllegalStateError) {
    // Handle business rule violations - these are EXPECTED (duplicate user, etc.)
    const alreadyExists = error.message.includes('already exists');
    wideEvent.trackError({
      type: 'BusinessRuleViolation',
      message: error.message,
      businessRule: {
        rule: 'illegal_state',
        violation: alreadyExists ? 'already_exists' : 'illegal_state',
      },
      unexpected: false, // Expected - business constraint violation
    });
  } else if (error instanceof HTTPException) {
    // Handle HTTP exceptions with proper status code categorization
    const isClientError = error.status >= 400 && error.status < 500;

    wideEvent.trackError({
      type: 'HTTPException',
      message: error.message,
      httpStatus: error.status,
      unexpected: !isClientError, // 4xx = expected (client error), 5xx = unexpected (server error)
    });
  } else if (isDeltaBaseError(error)) {
    // Categorize based on HTTP status code and error type
    const isServerError = error.status >= 500;

    // Business logic errors (4xx) are usually expected, system errors (5xx) are unexpected
    const unexpected =
      isServerError ||
      ['INTERNAL_SERVER_ERROR', 'STORAGE_ERROR', 'TIMEOUT_ERROR'].includes(
        error.errorType
      );

    const errorData: any = {
      type: error.errorType,
      message: error.message,
      httpStatus: error.status,
      unexpected,
    };

    // Include additional details if present
    if (error.details && Object.keys(error.details).length > 0) {
      errorData.details = error.details;
    }

    // Add specific context based on error type
    if (
      error.errorType === 'Version conflict' ||
      error.errorType === 'STREAM_VERSION_CONFLICT'
    ) {
      errorData.concurrency = {
        type: 'version_conflict',
        details: error.details,
      };
    } else if (
      error.errorType === 'AUTHENTICATION_FAILED' ||
      error.errorType === 'AUTHORIZATION_FAILED'
    ) {
      errorData.security = {
        type: error.errorType.toLowerCase(),
        details: error.details,
      };
    } else if (error.errorType === 'RATE_LIMIT_EXCEEDED') {
      errorData.rateLimit = {
        retryAfter: error.details?.retryAfter,
      };
    } else if (
      error.errorType === 'NOT_FOUND' ||
      error.errorType.endsWith('_NOT_FOUND')
    ) {
      errorData.resource = {
        type: error.details?.resourceType || 'unknown',
        id:
          error.details?.resourceId ||
          error.details?.eventStoreId ||
          error.details?.subscriptionId,
      };
    }

    wideEvent.trackError(errorData);
    return;
  } else {
    wideEvent.trackError({
      type: 'UnknownError',
      message: error.message,
      unexpected: true,
    });
  }
}

/**
 * Create an appropriate HTTP response based on the error type.
 *
 * @param error - The error that occurred
 * @param c - Hono context
 * @returns HTTP Response object with appropriate status and body
 */
// biome-ignore lint/suspicious/noExplicitAny: result context could be anything
function createErrorResponse(error: Error, c: any): Response {
  if (error instanceof ZodError) {
    return c.json(
      {
        success: false,
        error: {
          issues: error.issues,
          name: error.name,
        },
      },
      400
    );
  }

  if (error instanceof ValidationError) {
    return c.json({ success: false, message: error.message }, 400);
  }

  if (error instanceof IllegalStateError) {
    const alreadyExists = error.message.includes('already exists');
    const status = alreadyExists ? 409 : 500;
    return c.json({ success: false, message: error.message }, status);
  }

  if (error instanceof HTTPException) {
    return c.json(
      {
        success: false,
        message: error.message,
      },
      error.status
    );
  }

  if (isDeltaBaseError(error)) {
    const responseBody: any = {
      success: false,
      error: {
        type: error.errorType,
        message: error.message,
      },
    };

    // Include additional details if present
    if (error.details && Object.keys(error.details).length > 0) {
      responseBody.error.details = error.details;
    }

    // Include the original body if it's structured data
    if (error.body && typeof error.body === 'object' && error.body !== null) {
      responseBody.error.body = error.body;
    }

    return c.json(responseBody, error.status);
  }

  console.error('falling back to generic error response', error);

  // Generic error response
  const message = error.message || 'Failed to process request';
  return c.json({ success: false, message }, 500);
}

/**
 * Create a custom observability middleware with specific configuration.
 *
 * Use this when you need to customize the behavior of the observability
 * middleware for specific routes or applications.
 *
 * @param options - Custom configuration for the middleware
 * @returns Configured observability middleware
 *
 * @example
 * ```typescript
 * const customObservability = createObservabilityMiddleware({
 *   serviceName: 'my-custom-service',
 *   autoTrackHttp: true,
 *   autoTrackErrorResponses: true
 * });
 *
 * app.use('*', customObservability);
 * ```
 */
export function createObservabilityMiddleware(options: ObservabilityOptions) {
  return observabilityMiddleware(options);
}

/**
 * Add a custom error response pattern to the global configuration.
 *
 * This allows you to extend the automatic error detection with domain-specific
 * error patterns that properly categorize errors as expected or unexpected.
 *
 * @param pattern - The error response pattern to add
 *
 * @example
 * ```typescript
 * addErrorPattern({
 *   name: 'CustomBusinessError',
 *   matcher: (body) => body?.error?.type === 'BUSINESS_RULE_VIOLATION',
 *   tracker: (wideEvent, body) => {
 *     wideEvent.trackError({
 *       type: 'BusinessRuleViolation',
 *       message: body.error.message,
 *       businessRule: {
 *         rule: body.error.code,
 *         violation: body.error.details
 *       },
 *       unexpected: false // Expected - business rule violation
 *     });
 *   }
 * });
 * ```
 */
export function addErrorPattern(pattern: ErrorResponsePattern): void {
  observabilityConfig.addErrorPattern(pattern);
}

/**
 * Add custom context schema fields to a specific category.
 *
 * This allows you to extend the context schema with domain-specific fields
 * that will be automatically extracted and tracked.
 *
 * @param category - The context category to extend
 * @param fields - Array of field names to add
 *
 * @example
 * ```typescript
 * addContextFields('user', ['role', 'organization_id']);
 * addContextFields('operation', ['tenant_id', 'workspace_id']);
 * ```
 */
export function addContextFields(
  category: keyof ContextSchemaConfig,
  fields: string[]
): void {
  observabilityConfig.addContextFields(category, fields);
}

/**
 * Utility function to extract user context from common request patterns.
 *
 * This can be used in route handlers to automatically extract and track
 * user information from request headers, JWT tokens, etc.
 *
 * @param request - HTTP request object
 * @returns User data extracted from request, or undefined if not available
 *
 * @example
 * ```typescript
 * app.get('/users', async (c) => {
 *   const tracker = c.get('wideEvent');
 *   const userContext = extractUserContext(c.req.raw);
 *   if (userContext) {
 *     tracker.trackUser(userContext);
 *   }
 * });
 * ```
 */
export function extractUserContext(
  request: Request
): { actorType: string; id?: string } | undefined {
  // Check for authorization header
  const authHeader = request.headers.get('authorization');
  if (authHeader) {
    if (authHeader.startsWith('Bearer ')) {
      // Could decode JWT here to extract user info
      return { actorType: 'authenticated_user' };
    }
    if (authHeader.startsWith('Basic ')) {
      return { actorType: 'basic_auth_user' };
    }
  }

  // Check for API key
  const apiKey = request.headers.get('x-api-key');
  if (apiKey) {
    return { actorType: 'api_key_user' };
  }

  // Default to public access
  return { actorType: 'public' };
}

/**
 * Utility function to track operation timing automatically.
 *
 * This function wraps an operation and automatically tracks its execution time.
 *
 * @param tracker - Wide events wrapper
 * @param operationName - Name of the operation being timed
 * @param fn - Function to execute and time
 * @returns Promise with the operation result
 *
 * @example
 * ```typescript
 * const result = await trackOperationTiming(tracker, 'database-query', async () => {
 *   return await database.query('SELECT * FROM users');
 * });
 * ```
 */
export async function trackOperationTiming<T>(
  tracker: WideEventsWrapper,
  operationName: string,
  fn: () => Promise<T>
): Promise<T> {
  const startTime = Date.now();
  const span = tracker.createOperationSpan(operationName);

  try {
    const result = await fn();
    const duration = Date.now() - startTime;

    span.setAttributes({
      'operation.duration_ms': duration,
      'operation.success': true,
    });

    tracker.recordEvent(`${operationName}.completed`, {
      duration_ms: duration,
    });

    return result;
  } catch (error) {
    const duration = Date.now() - startTime;

    span.setAttributes({
      'operation.duration_ms': duration,
      'operation.success': false,
    });

    if (error instanceof Error) {
      span.recordException(error);
    }

    tracker.recordEvent(`${operationName}.failed`, {
      duration_ms: duration,
      error: error instanceof Error ? error.message : String(error),
    });

    throw error;
  } finally {
    span.end();
  }
}
